import {
  createContext,
  useContext,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  FP_LINES_FILTERS,
  FP_TABLE_REPEAT_HEADER_ATTRIBUTE,
  FP_TABLE_SCOPES,
  type FpLinesFilter,
  type FpTableScope,
  type PublicQuote,
  type PublicQuoteLine,
  type PublicQuoteQuantityBreak,
  type PublicQuoteSectionLine,
} from "@forgepoint/shared";
import {
  REPEATED_HEADER_ATTRIBUTE,
  unitMarker,
} from "@/modules/publicQuote/components/pageBreaks";
import {
  filterLines,
  linesBeforeFirstSection,
  sectionMemberLines,
} from "./FpLines";
import { FpLineField } from "./FpLineField";
import { HIDDEN_BLOCK_CLASS } from "./FpShowWhen";
import { FpText } from "./FpText";
import { containerStyle, readContainerValues } from "./fpContainer";
import {
  applyColumnVisibility,
  cellIsFilled,
  cellRendersContent,
  directChildren,
  fpNodeId,
  gateElements,
  hasColumnRules,
  hasHeaderBand,
  hasRowRules,
  headerLines,
  parseBreakMode,
  parseHeaderMode,
  parseRuleStyle,
  parseStripeColor,
  parseTableColumn,
  proseStyle,
  secondaryRowRenders,
  TABLE_CELL_PADDING,
  type FpBreakMode,
  type FpGatedElement,
  type FpHeaderMode,
  type FpTableColumn,
  type FpTableRuleStyle,
} from "./fpTableColumns";
import {
  buildItemNumbers,
  interpolateLineTokens,
  parseNumberSourceAttribute,
} from "./lineFields";
import { LineScopeContext, useLineScope } from "./lineScopeContext";
import { useQuoteDocumentContext } from "./quoteDocumentContext";
import { shouldShowWhen } from "./showWhen";
import type { FpCommonProps } from "./types";

interface FpTableProps extends FpCommonProps {
  /** The `<fp-table>` element, read for its column and slot declarations. */
  element: Element;
  /** Converts a static `<fp-cell>`'s child nodes to React. */
  renderChildren: (parent: Node, keyPrefix: string) => ReactNode[];
}

/** Whether a gate condition holds in the caller's scope. */
type VisibilityGate = (condition: string) => boolean;

/** The `<fp-table>` attribute deciding whether headers inherit body style. */
const HEADER_MODE_ATTRIBUTE = "header-style";

/**
 * The slice of a line-bound `<fp-table>` one rendered page fragment covers.
 *
 * A fragmentable block holds exactly one such table, so the window is supplied
 * per block rather than keyed by table id: whichever `<fp-table>` reads it is
 * the one it describes.
 */
export interface FpTableUnitWindow {
  /** The table's first row unit, in the enclosing section's unit space. */
  baseUnitIndex: number;
  /**
   * The first row unit this fragment renders, relative to the table. Zero marks
   * the table's opening fragment, which is the one that draws the column-header
   * band unless the table opts into `repeat-header`.
   */
  startUnit: number;
  /** One past the last row unit this fragment renders, relative to the table. */
  endUnit: number;
}

/** The row-unit window the enclosing fragment imposes, or null when unsliced. */
export const FpTableUnitWindowContext = createContext<FpTableUnitWindow | null>(
  null,
);

/**
 * The row-unit window the enclosing fragment imposes on a line-bound table.
 * @returns The window, or null when the table renders whole
 */
function useFpTableUnitWindow(): FpTableUnitWindow | null {
  return useContext(FpTableUnitWindowContext);
}

/**
 * Renders `<fp-table>`: the proposed vocabulary's single tabular primitive. It
 * owns its own repetition - `scope="sections"` groups the quote's lines under
 * their section heading and notes, `scope="flat"` lists them ungrouped, and a
 * table declaring neither `scope` nor `filter` declares static
 * `<fp-row>`/`<fp-cell>` children instead.
 * Its `<fp-column>`, `<fp-row>` and `<fp-cell>` children are read here rather
 * than converted, so their `data-fp-show-when` gates are resolved here too, and
 * each one's `data-fp-node` rides onto the element it renders so the editor can
 * select it in its own right. A table declaring `padding` or `margin` renders
 * inside a box carrying it, since neither behaves on a table laid out at full
 * width.
 * @param props - The table element, the static-cell converter, and the style
 * @returns The rendered table, wrapped when it declares padding or margin
 */
export function FpTable({
  element,
  renderChildren,
  className,
  style,
  "data-fp-node": dataFpNode,
}: FpTableProps) {
  const { quote, editable } = useQuoteDocumentContext();
  const lineScope = useLineScope();
  const unitWindow = useFpTableUnitWindow();
  const declaredColumns = directChildren(element, "fp-column").map(
    parseTableColumn,
  );
  const tableScope = parseTableScope(element.getAttribute("scope"));
  const rule = parseRuleStyle(
    element.getAttribute("rules"),
    element.getAttribute("rule-color"),
  );
  const fillHeight =
    unitWindow === null &&
    (element.hasAttribute("height") || element.hasAttribute("min-height"));
  const { outer, inner } = splitTablePadding(
    unitWindow === null ? style : withoutMinHeight(style),
  );
  const box = Object.keys(outer).length === 0 ? null : outer;
  const tableStyle: CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    ...inner,
  };

  const boundToLines =
    element.hasAttribute("scope") || element.hasAttribute("filter");
  const staticRowElements = directChildren(element, "fp-row");
  if (
    !boundToLines &&
    (staticRowElements.length > 0 || declaredColumns.length === 0)
  ) {
    const gate: VisibilityGate = (condition) =>
      shouldShowWhen(quote, lineScope, condition);
    return padded(
      box,
      className,
      <table
        className={box === null ? className : undefined}
        style={{
          width: "100%",
          borderCollapse: "collapse",
          tableLayout: "fixed",
          ...inner,
        }}
        data-fp-node={dataFpNode}
      >
        {staticGrid(gateElements(staticRowElements, gate, editable), {
          ruler: applyColumnVisibility(declaredColumns, gate, editable),
          rule,
          fillHeight,
          editable,
          gate,
          renderChildren,
        })}
      </table>,
    );
  }

  const filter = parseTableFilter(element.getAttribute("filter"));
  const itemNumbers = buildItemNumbers(
    quote,
    parseNumberSourceAttribute(element.getAttribute("number-source")),
  );
  const boundLines = tableLines(quote, tableScope, filter);
  const columns = applyColumnVisibility(
    declaredColumns,
    (condition) => columnGateHolds(quote, boundLines, itemNumbers, condition),
    editable,
  );
  const mainColumns = columns.filter((column) => column.row === 1);
  const layout: TableLayout = {
    mainColumns,
    secondaryRows: groupSecondaryRows(columns),
    breakMode: parseBreakMode(element.getAttribute("break-mode")),
    itemNumbers,
    rule,
    stripe: stripeBands(
      parseStripeColor(element.getAttribute("stripe")),
      boundLines,
    ),
    quote,
    editable,
  };

  const repeatHeader = repeatsHeader(element);
  const rowUnits = tableRowUnits(element, quote, tableScope, filter);
  const visibleUnits =
    unitWindow === null
      ? rowUnits
      : rowUnits.slice(unitWindow.startUnit, unitWindow.endUnit);
  const firstUnitIndex =
    unitWindow === null
      ? null
      : unitWindow.baseUnitIndex + unitWindow.startUnit;
  const slots: FpTableSlots = {
    heading: directChildren(element, "fp-section-heading")[0] ?? null,
    notes: directChildren(element, "fp-section-notes")[0] ?? null,
  };

  return (
    <FpTableUnitWindowContext.Provider value={null}>
      {padded(
        box,
        className,
        <table
          className={box === null ? className : undefined}
          style={tableStyle}
          data-fp-node={dataFpNode}
        >
          {columnGroup(mainColumns.length, mainColumns)}
          {unitWindow === null || unitWindow.startUnit === 0 || repeatHeader
            ? headerRow(
                mainColumns,
                rule,
                parseHeaderMode(element.getAttribute(HEADER_MODE_ATTRIBUTE)),
                repeatHeader,
              )
            : null}
          <tbody>
            {visibleUnits.map((unit, index) =>
              rowUnitNode(
                unit,
                firstUnitIndex === null ? null : firstUnitIndex + index,
                slots,
                layout,
              ),
            )}
            {fillHeight ? spacerRow(mainColumns.length, rule) : null}
          </tbody>
        </table>,
      )}
    </FpTableUnitWindowContext.Provider>
  );
}

/** The slot elements a `scope="sections"` table renders its groups through. */
interface FpTableSlots {
  heading: Element | null;
  notes: Element | null;
}

/**
 * The rows of one unit. The unit index rides onto the first `<tr>` it produces,
 * which is how the paginator learns where the unit starts and so where it may
 * break the table.
 * @param unit - The row unit to render
 * @param unitIndex - The unit's index, or null outside a fragmented render
 * @param slots - The table's section heading and notes slots
 * @param layout - The column declarations and break mode
 * @returns The unit's rows
 */
function rowUnitNode(
  unit: FpTableRowUnit,
  unitIndex: number | null,
  slots: FpTableSlots,
  layout: TableLayout,
): ReactNode {
  if (unit.kind === "section-heading") {
    return sectionHeadingRow(unit.section, slots.heading, layout, unitIndex);
  }
  if (unit.kind === "section-note") {
    return sectionNoteRow(
      unit.section,
      unit.note,
      slots.notes,
      layout,
      unitIndex,
    );
  }
  return (
    <LineRowGroup
      key={unit.line.id}
      line={unit.line}
      section={unit.section}
      layout={layout}
      unitIndex={unitIndex}
    />
  );
}

/**
 * One atomic group of rows a line-bound table renders. A unit is never split
 * across a page: one quote line's main row and every secondary row it fills are
 * a single unit, as are a `break-mode="rows"` line's per-break rows.
 */
type FpTableRowUnit =
  | { kind: "section-heading"; section: PublicQuoteSectionLine }
  | {
      kind: "section-note";
      section: PublicQuoteSectionLine | null;
      note: PublicQuoteLine;
    }
  | {
      kind: "line";
      section: PublicQuoteSectionLine | null;
      line: PublicQuoteLine;
    };

/**
 * The row units a line-bound table renders, in document order: under
 * `scope="sections"` the unsectioned note rows and filtered lines before the
 * first section, then each section's heading, its note rows, and its filtered
 * lines; otherwise the filtered lines alone. The table renders from this list
 * and the fragment planner counts it, so the two agree on the unit space by
 * construction rather than by convention.
 * @param element - The `<fp-table>` element, read for its section slots
 * @param quote - The public quote backing the document
 * @param scope - The table's declared scope, or null when it declares none
 * @param filter - The table's line-type filter
 * @returns The row units, in document order
 */
function tableRowUnits(
  element: Element,
  quote: PublicQuote,
  scope: FpTableScope | null,
  filter: FpLinesFilter,
): FpTableRowUnit[] {
  if (scope !== "sections") {
    return lineUnits(filterLines(quote.lines, filter), null);
  }
  const headingSlot = directChildren(element, "fp-section-heading")[0] ?? null;
  const notesSlot = directChildren(element, "fp-section-notes")[0] ?? null;
  const leadingLines = linesBeforeFirstSection(quote.lines);
  const sectionUnits = quote.lines.filter(isSectionLine).flatMap((section) => {
    const members = sectionMemberLines(quote.lines, section);
    return [
      ...headingUnits(section, headingSlot),
      ...noteUnits(section, members, notesSlot),
      ...lineUnits(filterLines(members, filter), section),
    ];
  });
  return [
    ...noteUnits(null, leadingLines, notesSlot),
    ...lineUnits(filterLines(leadingLines, filter), null),
    ...sectionUnits,
  ];
}

/**
 * The heading unit of one section, or none when the table declares no heading
 * slot or the section carries no heading text.
 * @param section - The SECTION line
 * @param slot - The `<fp-section-heading>` element, or null when absent
 * @returns The heading unit, or an empty list
 */
function headingUnits(
  section: PublicQuoteSectionLine,
  slot: Element | null,
): FpTableRowUnit[] {
  if (slot === null || section.description.trim() === "") {
    return [];
  }
  return [{ kind: "section-heading", section }];
}

/**
 * The note units of one section: one per NOTE line carrying text.
 * @param section - The section the notes belong to, or null when unsectioned
 * @param members - The lines the notes are drawn from
 * @param slot - The `<fp-section-notes>` element, or null when absent
 * @returns The note units, in document order
 */
function noteUnits(
  section: PublicQuoteSectionLine | null,
  members: readonly PublicQuoteLine[],
  slot: Element | null,
): FpTableRowUnit[] {
  if (slot === null) {
    return [];
  }
  return members
    .filter((line) => line.type === "NOTE" && line.description.trim() !== "")
    .map((note) => ({ kind: "section-note", section, note }));
}

/**
 * One unit per priced line.
 * @param lines - The lines the table renders
 * @param section - The enclosing section, or null in a flat table
 * @returns The line units, in document order
 */
function lineUnits(
  lines: readonly PublicQuoteLine[],
  section: PublicQuoteSectionLine | null,
): FpTableRowUnit[] {
  return lines.map((line) => ({ kind: "line", section, line }));
}

/**
 * How many units a line-bound table occupies: the count the fragment planner
 * reserves in the enclosing section's unit space.
 * @param element - The `<fp-table>` element
 * @param quote - The public quote backing the document
 * @returns The table's unit count
 */
export function tableUnitCount(element: Element, quote: PublicQuote): number {
  return tableRowUnits(
    element,
    quote,
    parseTableScope(element.getAttribute("scope")),
    parseTableFilter(element.getAttribute("filter")),
  ).length;
}

/**
 * Parses the table's `filter`, defaulting to the quote's item lines.
 * @param value - The raw `filter` attribute value, or null when absent
 * @returns The line-type filter
 */
function parseTableFilter(value: string | null): FpLinesFilter {
  return FP_LINES_FILTERS.find((candidate) => candidate === value) ?? "items";
}

/**
 * Parses the table's `scope`, which decides whether it repeats the quote's lines
 * and how it groups them. Validation rejects an unrecognised value on save, so
 * one is read as a table that declared no scope at all.
 * @param value - The raw `scope` attribute value, or null when absent
 * @returns The declared scope, or null when the table declares none
 */
function parseTableScope(value: string | null): FpTableScope | null {
  return FP_TABLE_SCOPES.find((candidate) => candidate === value) ?? null;
}

/**
 * Narrows a quote line to a SECTION line.
 * @param line - The quote line
 * @returns True when the line is a SECTION line
 */
function isSectionLine(line: PublicQuoteLine): line is PublicQuoteSectionLine {
  return line.type === "SECTION";
}

/**
 * Splits a table's authored container style into what belongs outside its
 * border box and what stays on the table. CSS discards padding set on a table
 * in the collapsing border model, and the table itself is laid out at
 * `width: 100%`, which over-constrains a horizontal margin so it shifts the
 * table out of its parent instead of inseting it. Both move to a wrapper box,
 * where they behave the way they do on every other block.
 *
 * The `auto` side margins `align` produces stay on the table: they position it
 * against its own authored width, which the wrapper does not have. Everything
 * else stays too, so the table keeps collapsing its cell borders and a table
 * whose columns draw their own borders still shows them as hairlines rather
 * than doubling them up.
 * @param style - The table's authored container style, or undefined when absent
 * @returns The wrapper's box style and the style left on the table
 */
/**
 * Drops a declared minimum height from a windowed fragment's style: the table
 * only fragments once it overflows a page, so its minimum is already met, and
 * padding each per-page sub-table up to it would stretch bogus space instead.
 * @param style - The table's declared style
 * @returns The style without its `minHeight`
 */
function withoutMinHeight(style: CSSProperties | undefined): CSSProperties {
  const { minHeight: _minHeight, ...rest } = style ?? {};
  return rest;
}

function splitTablePadding(style: CSSProperties | undefined): {
  outer: CSSProperties;
  inner: CSSProperties;
} {
  const declared: CSSProperties = style ?? {};
  const {
    padding,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    margin,
    ...inner
  } = declared;
  return {
    outer: {
      ...(padding === undefined ? {} : { padding }),
      ...(paddingTop === undefined ? {} : { paddingTop }),
      ...(paddingRight === undefined ? {} : { paddingRight }),
      ...(paddingBottom === undefined ? {} : { paddingBottom }),
      ...(paddingLeft === undefined ? {} : { paddingLeft }),
      ...(margin === undefined ? {} : { margin }),
    },
    inner,
  };
}

/**
 * The table inside the box carrying its padding and margin, or the bare table
 * when it declares neither, so a table with no box props adds no element to the
 * render. The block's class lands on the wrapper rather than the table it
 * encloses.
 * @param outer - The wrapper box's style, or null when there is none
 * @param className - The class the caller injected, or undefined
 * @param table - The rendered table, already carrying the class when unwrapped
 * @returns The wrapped table
 */
function padded(
  outer: CSSProperties | null,
  className: string | undefined,
  table: ReactElement,
): ReactElement {
  if (outer === null) {
    return table;
  }
  return (
    <div className={className} style={outer}>
      {table}
    </div>
  );
}

/** Everything the row builders need beyond the lines themselves. */
interface TableLayout {
  mainColumns: FpTableColumn[];
  /** The `row="N"` columns, grouped into one entry per secondary row. */
  secondaryRows: FpTableColumn[][];
  breakMode: FpBreakMode;
  itemNumbers: Map<string, string>;
  /** How the table draws the rules between its cells. */
  rule: FpTableRuleStyle;
  /** The zebra band the table paints, or null when it declares no stripe. */
  stripe: FpTableStripe | null;
  quote: PublicQuote;
  /** True in the editor, where an empty conditional row stays drawn. */
  editable: boolean;
}

/**
 * The lines a bound table renders, which is the population its column gates are
 * evaluated over.
 * @param quote - The public quote backing the document
 * @param tableScope - The table's declared scope, or null when it declares none
 * @param filter - The table's line-type filter
 * @returns The rendered lines, in document order
 */
function tableLines(
  quote: PublicQuote,
  tableScope: FpTableScope | null,
  filter: FpLinesFilter,
): PublicQuoteLine[] {
  if (tableScope !== "sections") {
    return filterLines(quote.lines, filter);
  }
  const sectionMembers = quote.lines
    .filter(isSectionLine)
    .map((section) => sectionMemberLines(quote.lines, section));
  return [linesBeforeFirstSection(quote.lines), ...sectionMembers].flatMap(
    (lines) => filterLines(lines, filter),
  );
}

/** The colour a table stripes with, and the lines that band falls on. */
interface FpTableStripe {
  color: string;
  /** The ids of the lines painted, which is every other rendered line. */
  lineIds: ReadonlySet<string>;
}

/**
 * The zebra band a striped table paints. A line is the unit of alternation
 * rather than the `<tr>`, so the extra rows one line occupies - its secondary
 * rows, and its per-break rows under `break-mode="rows"` - read as one band
 * instead of striping against themselves. Section headings and notes render
 * outside this population, so they neither take a band nor shift the next line's.
 * @param color - The table's stripe colour, or null when it declares none
 * @param lines - The lines the table renders, in document order
 * @returns The band, or null when the table declares no stripe
 */
function stripeBands(
  color: string | null,
  lines: readonly PublicQuoteLine[],
): FpTableStripe | null {
  if (color === null) {
    return null;
  }
  const painted = lines.filter((_line, index) => index % 2 === 1);
  return { color, lineIds: new Set(painted.map((line) => line.id)) };
}

/**
 * Whether a column's gate holds anywhere in the table: for at least one line it
 * renders, or at quote scope when it renders none. This decides only whether the
 * column exists in the grid, which every line shares and so must agree on; each
 * line then decides its own cell through `cellRendersContent`.
 * @param quote - The public quote backing the document
 * @param lines - The lines the table renders
 * @param itemNumbers - The quote's line-id to display-number map
 * @param condition - The column's `data-fp-show-when` value
 * @returns True when the column takes a place in the grid
 */
function columnGateHolds(
  quote: PublicQuote,
  lines: readonly PublicQuoteLine[],
  itemNumbers: Map<string, string>,
  condition: string,
): boolean {
  if (lines.length === 0) {
    return shouldShowWhen(quote, null, condition);
  }
  return lines.some((line) =>
    shouldShowWhen(
      quote,
      {
        line,
        itemNumber: itemNumbers.get(line.id) ?? null,
        section: null,
        quantityBreak: null,
      },
      condition,
    ),
  );
}

/**
 * Groups the `row="N"` columns into one list per secondary row, in row order.
 * @param columns - Every column declared on the table
 * @returns The secondary rows, each holding its columns in document order
 */
function groupSecondaryRows(columns: FpTableColumn[]): FpTableColumn[][] {
  const rows = [...new Set(columns.map((column) => column.row))]
    .filter((row) => row > 1)
    .sort((left, right) => left - right);
  return rows.map((row) => columns.filter((column) => column.row === row));
}

/**
 * The `<thead>` titling the main columns, or nothing when none declares a header.
 * The rule under the header is the header's own and is drawn whatever `rules`
 * says, because it separates the titles from the lines rather than one line from
 * the next; a column painting its own header band replaces that rule with the
 * band. Body style reaches the header only under `header-style="inherit"`, and
 * each column's `header-*` overrides land last either way.
 * @param mainColumns - The main-row column declarations
 * @param rule - How the table draws the rules between its cells
 * @param mode - Whether header cells start from their column's body style
 * @param repeated - Whether the band reprints on every page, which marks it for
 * the paginator to charge as per-page overhead
 * @returns The header row, or null
 */
function headerRow(
  mainColumns: FpTableColumn[],
  rule: FpTableRuleStyle,
  mode: FpHeaderMode,
  repeated: boolean,
): ReactNode {
  if (!mainColumns.some((column) => column.header !== null)) {
    return null;
  }
  return (
    <thead {...(repeated ? { [REPEATED_HEADER_ATTRIBUTE]: "" } : {})}>
      <tr>
        {mainColumns.map((column, index) => (
          <th
            key={index}
            data-fp-node={column.nodeId}
            className={hiddenClass(column.hidden)}
            style={{
              width: column.width ?? undefined,
              textAlign: column.align ?? "left",
              padding: TABLE_CELL_PADDING,
              borderBottom: hasHeaderBand(column)
                ? undefined
                : "1px solid #111827",
              ...columnRule(rule, index === mainColumns.length - 1),
              fontWeight: 600,
              verticalAlign: "bottom",
              ...(mode === "inherit" ? column.style : {}),
              ...column.headerStyle,
            }}
          >
            {headerContent(column.header)}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * Whether the table reprints its column-header band on every page it spans.
 * @param element - The `<fp-table>` element
 * @returns True when the band repeats
 */
function repeatsHeader(element: Element): boolean {
  return (
    element.hasAttribute(FP_TABLE_REPEAT_HEADER_ATTRIBUTE) &&
    element.getAttribute(FP_TABLE_REPEAT_HEADER_ATTRIBUTE) !== "false"
  );
}

/**
 * A column header's content: one stacked line per `|`-separated part.
 * @param header - The raw `header` attribute value, or null when absent
 * @returns The header content
 */
function headerContent(header: string | null): ReactNode {
  const lines = headerLines(header);
  if (lines.length < 2) {
    return lines[0] ?? "";
  }
  return lines.map((line, index) => <div key={index}>{line}</div>);
}

/**
 * The row rendering a section's heading, or nothing when the table declares no
 * heading slot or the section carries no heading.
 * @param section - The SECTION line
 * @param slot - The `<fp-section-heading>` element, or null when absent
 * @param layout - The column declarations, for the span width
 * @param unitIndex - The row's unit index, or null outside a fragmented render
 * @returns The heading row, or null
 */
function sectionHeadingRow(
  section: PublicQuoteSectionLine,
  slot: Element | null,
  layout: TableLayout,
  unitIndex: number | null,
): ReactNode {
  if (slot === null || section.description.trim() === "") {
    return null;
  }
  return (
    <tr key={`heading-${section.id}`} {...unitMarker(unitIndex)}>
      <td
        data-fp-node={fpNodeId(slot)}
        colSpan={Math.max(layout.mainColumns.length, 1)}
        style={{
          padding: "12px 6px 4px",
          fontWeight: 700,
          ...containerStyle(readContainerValues(slot)),
        }}
      >
        {section.description}
      </td>
    </tr>
  );
}

/**
 * The row rendering one of a section's NOTE lines as markdown, or nothing when
 * the table declares no notes slot. The slot's box style stays on the cell while
 * its text style rides onto the prose, which sets its own colour and font and
 * would otherwise paint over whatever the slot declared.
 * @param section - The SECTION line the note belongs to, or null when the quote
 *   carries no sections
 * @param note - The NOTE line to render
 * @param slot - The `<fp-section-notes>` element, or null when absent
 * @param layout - The column declarations, for the span width
 * @param unitIndex - The row's unit index, or null outside a fragmented render
 * @returns The note row, or null
 */
function sectionNoteRow(
  section: PublicQuoteSectionLine | null,
  note: PublicQuoteLine,
  slot: Element | null,
  layout: TableLayout,
  unitIndex: number | null,
): ReactNode {
  if (slot === null) {
    return null;
  }
  const slotStyle = containerStyle(readContainerValues(slot));
  return (
    <tr key={`note-${note.id}`} {...unitMarker(unitIndex)}>
      <td
        data-fp-node={fpNodeId(slot)}
        colSpan={Math.max(layout.mainColumns.length, 1)}
        style={{ padding: "2px 6px 6px", ...slotStyle }}
      >
        <LineScopeContext.Provider
          value={{
            line: note,
            itemNumber: null,
            section,
            quantityBreak: null,
          }}
        >
          <FpText
            source="description"
            content=""
            style={proseStyle(slotStyle)}
          />
        </LineScopeContext.Provider>
      </td>
    </tr>
  );
}

interface LineRowGroupProps {
  line: PublicQuoteLine;
  section: PublicQuoteSectionLine | null;
  layout: TableLayout;
  /** The group's unit index, or null outside a fragmented render. */
  unitIndex: number | null;
}

/**
 * The rows one quote line occupies: its main row (or one row per quantity break
 * under `break-mode="rows"`), then whichever secondary rows this line actually
 * fills. A line that fills none seals its own main row, so the rule under it
 * lands where the line ends rather than under a blank sub-row.
 *
 * The whole group is one page unit, marked on its first row: a line's secondary
 * and per-break rows never land on a different page from the line itself.
 * @param props - The line, its section, the table layout, and its unit index
 * @returns The line's rows, wrapped so they stay together on a page
 */
function LineRowGroup({ line, section, layout, unitIndex }: LineRowGroupProps) {
  const breaks = lineBreaks(line);
  const scopeBase = {
    line,
    itemNumber: layout.itemNumbers.get(line.id) ?? null,
    section,
  };
  const filledRows = layout.secondaryRows.filter((columns) =>
    secondaryRowRenders(
      layout.quote,
      { ...scopeBase, quantityBreak: null },
      columns,
      layout.editable,
    ),
  );
  const sealed = filledRows.length === 0;
  const splitByBreak = layout.breakMode === "rows" && breaks.length > 1;
  return (
    <>
      {splitByBreak
        ? breaks.map((quantityBreak, index) => (
            <tr
              key={`break-${index}`}
              style={stripeStyle(layout, line)}
              {...unitMarker(index === 0 ? unitIndex : null)}
            >
              {layout.mainColumns.map((column, columnIndex) =>
                breakModeCell(
                  column,
                  columnIndex,
                  { index, count: breaks.length, sealed },
                  { ...scopeBase, quantityBreak },
                  layout,
                ),
              )}
            </tr>
          ))
        : mainRow(layout, scopeBase, breaks, sealed, unitIndex)}
      {filledRows.map((row, index) =>
        secondaryRow(row, layout, scopeBase, {
          index,
          count: filledRows.length,
        }),
      )}
    </>
  );
}

/** Where one row sits in a repeating run, which decides whether it seals it. */
interface RowPosition {
  index: number;
  count: number;
}

/** The scope members a cell's provider needs beyond the quantity break. */
interface CellScopeBase {
  line: PublicQuoteLine;
  itemNumber: string | null;
  section: PublicQuoteSectionLine | null;
}

/**
 * The quantity breaks a line renders: an item line's own breaks, else a single
 * null break so non-item rows still render one row.
 * @param line - The quote line
 * @returns The breaks to render, never empty
 */
function lineBreaks(
  line: PublicQuoteLine,
): (PublicQuoteQuantityBreak | null)[] {
  if (line.type !== "ITEM" || line.quantityBreaks.length === 0) {
    return [null];
  }
  return [...line.quantityBreaks];
}

/**
 * The style banding one line's rows, or nothing when the table declares no
 * stripe or this line falls between bands. The colour sits on the row rather
 * than its cells so a cell's own authored `background` still paints over it,
 * the stripe being only the table's default.
 * @param layout - The table layout, read for the stripe it paints
 * @param line - The line the row belongs to
 * @returns The row style, or undefined when the row takes no band
 */
function stripeStyle(
  layout: TableLayout,
  line: PublicQuoteLine,
): CSSProperties | undefined {
  if (layout.stripe === null || !layout.stripe.lineIds.has(line.id)) {
    return undefined;
  }
  return { backgroundColor: layout.stripe.color };
}

/**
 * The line's single main row under `break-mode="stack"`: a break-repeating
 * column stacks one value per break inside its cell.
 * @param layout - The column declarations and break mode
 * @param scopeBase - The line, its number, and its section
 * @param breaks - The line's quantity breaks
 * @param sealed - Whether the line draws no secondary row under this one
 * @param unitIndex - The group's unit index, or null outside a fragmented render
 * @returns The main row
 */
function mainRow(
  layout: TableLayout,
  scopeBase: CellScopeBase,
  breaks: (PublicQuoteQuantityBreak | null)[],
  sealed: boolean,
  unitIndex: number | null,
): ReactNode {
  return (
    <tr style={stripeStyle(layout, scopeBase.line)} {...unitMarker(unitIndex)}>
      {layout.mainColumns.map((column, index) => (
        <td
          key={index}
          data-fp-node={column.nodeId}
          className={hiddenClass(column.hidden)}
          style={cellStyle(
            column,
            layout.rule,
            sealed,
            index === layout.mainColumns.length - 1,
          )}
        >
          {column.repeatBreaks ? (
            breaks.map((quantityBreak, breakIndex) => (
              <div key={breakIndex}>
                <ScopedCell
                  column={column}
                  scope={{ ...scopeBase, quantityBreak }}
                />
              </div>
            ))
          ) : (
            <ScopedCell
              column={column}
              scope={{ ...scopeBase, quantityBreak: null }}
            />
          )}
        </td>
      ))}
    </tr>
  );
}

/**
 * One cell of a `break-mode="rows"` row: a break-repeating column renders on
 * every row, a plain column renders once and spans the line's break rows.
 *
 * Both kinds seal on the same edge. A spanning cell's bottom border already sits
 * at the foot of the whole group, so it takes the group's own seal; a repeating
 * cell takes it only on the last break row. Sealing the spanning cells alone
 * would stop the rule halfway across a line that carries a secondary row.
 * @param column - The column declaration
 * @param columnIndex - The column's position, used as the React key
 * @param position - The break row's index and count, and the group's seal
 * @param scope - The cell's line scope
 * @param layout - The table layout, for the rule style and column count
 * @returns The cell, or null when a spanning cell was already drawn
 */
function breakModeCell(
  column: FpTableColumn,
  columnIndex: number,
  position: RowPosition & { sealed: boolean },
  scope: CellScopeBase & { quantityBreak: PublicQuoteQuantityBreak | null },
  layout: TableLayout,
): ReactNode {
  if (!column.repeatBreaks && position.index > 0) {
    return null;
  }
  const sealed = column.repeatBreaks
    ? position.sealed && position.index === position.count - 1
    : position.sealed;
  return (
    <td
      key={columnIndex}
      data-fp-node={column.nodeId}
      className={hiddenClass(column.hidden)}
      rowSpan={column.repeatBreaks ? undefined : position.count}
      style={cellStyle(
        column,
        layout.rule,
        sealed,
        columnIndex === layout.mainColumns.length - 1,
      )}
    >
      <ScopedCell column={column} scope={scope} />
    </td>
  );
}

/**
 * One of the line's secondary rows, built from the columns declaring that row.
 * The row tucks under the main row by losing its top padding, unless the column
 * asked for padding of its own.
 * @param columns - The columns sitting on this secondary row
 * @param layout - The table layout, for the main column count
 * @param scopeBase - The line, its number, and its section
 * @param position - The row's place among the line's rendered secondary rows
 * @returns The secondary row
 */
function secondaryRow(
  columns: FpTableColumn[],
  layout: TableLayout,
  scopeBase: CellScopeBase,
  position: RowPosition,
): ReactNode {
  const mainCount = Math.max(layout.mainColumns.length, 1);
  const last = position.index === position.count - 1;
  const declared = columns.reduce(
    (total, column) => total + cellSpan(column, mainCount),
    0,
  );
  return (
    <tr key={position.index} style={stripeStyle(layout, scopeBase.line)}>
      {columns.map((column, columnIndex) => (
        <td
          key={columnIndex}
          data-fp-node={column.nodeId}
          className={hiddenClass(column.hidden)}
          colSpan={cellSpan(column, mainCount)}
          style={{
            ...cellStyle(
              column,
              layout.rule,
              last,
              columnIndex === columns.length - 1 && declared >= mainCount,
            ),
            ...(column.style.padding === undefined ? { paddingTop: 0 } : {}),
            whiteSpace: "pre-line",
          }}
        >
          <ScopedCell
            column={column}
            scope={{ ...scopeBase, quantityBreak: null }}
          />
        </td>
      ))}
      {declared < mainCount ? (
        <td
          colSpan={mainCount - declared}
          style={{ borderBottom: rowRule(layout.rule, last) }}
        />
      ) : null}
    </tr>
  );
}

/**
 * How many columns one cell spans.
 * @param column - The column declaration
 * @param mainCount - The number of main-row columns
 * @returns The cell's column span
 */
function cellSpan(column: FpTableColumn, mainCount: number): number {
  if (column.span === "all") {
    return mainCount;
  }
  return column.span === null ? 1 : Math.min(column.span, mainCount);
}

interface ScopedCellProps {
  column: FpTableColumn;
  scope: CellScopeBase & { quantityBreak: PublicQuoteQuantityBreak | null };
}

/**
 * A column's cell content, resolved against one line (and one quantity break):
 * its `label` on its own line, then `prefix`, the bound value, and `suffix` as
 * one run of text. The literals interpolate `{{ source }}` line-field tokens,
 * so a second bound value can ride in the same cell
 * (`suffix="{{ weight-unit }}"`). A column declaring only literal text and no
 * `source` is a pure label column.
 *
 * The column's gate is re-resolved here against this line, so a column the table
 * keeps for the lines that fill it stays empty on the lines that do not, and an
 * unfilled cell prints nothing rather than its literal text alone. The editor
 * still draws the binding's placeholder chip, which is what keeps an empty cell
 * legible and selectable while authoring.
 * @param props - The column declaration and the line scope to resolve against
 * @returns The cell's literal text and bound line field
 */
function ScopedCell({ column, scope }: ScopedCellProps) {
  const { quote, editable } = useQuoteDocumentContext();
  if (!cellRendersContent(quote, scope, column)) {
    return null;
  }
  const filled = cellIsFilled(quote, scope, column);
  if (!filled && !editable) {
    return null;
  }
  const label = interpolateLineTokens(quote, scope, column.label);
  return (
    <LineScopeContext.Provider value={scope}>
      {filled && label !== null ? <div>{label}</div> : null}
      {filled ? interpolateLineTokens(quote, scope, column.prefix) : null}
      {column.source === null ? null : <FpLineField source={column.source} />}
      {filled ? interpolateLineTokens(quote, scope, column.suffix) : null}
    </LineScopeContext.Provider>
  );
}

/**
 * The inline style of one body cell. The column's own container style lands
 * last, so a column declaring `padding` replaces the table's default cell
 * padding rather than fighting it. A long unbroken token breaks instead of
 * spilling out, since a fixed layout no longer widens the column to fit it.
 * @param column - The column declaration
 * @param rule - How the table draws the rules between its cells
 * @param sealed - Whether the cell closes the line's row group
 * @param lastColumn - Whether the cell sits against the table's right edge
 * @returns The cell style
 */
function cellStyle(
  column: FpTableColumn,
  rule: FpTableRuleStyle,
  sealed: boolean,
  lastColumn: boolean,
): CSSProperties {
  return {
    width: column.width ?? undefined,
    textAlign: column.align,
    padding: TABLE_CELL_PADDING,
    verticalAlign: "top",
    overflowWrap: "break-word",
    borderBottom: rowRule(rule, sealed),
    ...columnRule(rule, lastColumn),
    ...column.style,
  };
}

/**
 * The rule drawn under a cell, or nothing when the table draws no row rules or
 * the cell does not close its row group.
 * @param rule - How the table draws the rules between its cells
 * @param sealed - Whether the cell closes the line's row group
 * @returns The CSS border value, or undefined
 */
function rowRule(rule: FpTableRuleStyle, sealed: boolean): string | undefined {
  if (!sealed || !hasRowRules(rule.model)) {
    return undefined;
  }
  return `1px solid ${rule.color}`;
}

/**
 * The rule drawn down a cell's right edge, or nothing when the table draws no
 * column rules or the cell already sits against the table's own border.
 * @param rule - How the table draws the rules between its cells
 * @param lastColumn - Whether the cell sits against the table's right edge
 * @returns The style fragment
 */
function columnRule(
  rule: FpTableRuleStyle,
  lastColumn: boolean,
): CSSProperties {
  if (lastColumn || !hasColumnRules(rule.model)) {
    return {};
  }
  return { borderRight: `1px solid ${rule.color}` };
}

/**
 * The editor-only marker on a grid element whose `data-fp-show-when` failed.
 * @param hidden - Whether the element's gate resolved false
 * @returns The hidden-block class, or undefined
 */
function hiddenClass(hidden: boolean): string | undefined {
  return hidden ? HIDDEN_BLOCK_CLASS : undefined;
}

/** Everything the static-table rows need beyond the rows themselves. */
interface StaticTableLayout {
  /** The table's `<fp-column>` declarations, acting as a positional ruler. */
  ruler: FpTableColumn[];
  rule: FpTableRuleStyle;
  /** Whether a spacer row must soak up surplus table height. */
  fillHeight: boolean;
  editable: boolean;
  gate: VisibilityGate;
  renderChildren: (parent: Node, keyPrefix: string) => ReactNode[];
}

/**
 * The grid of a static table: its `<fp-row>` children, whose `<fp-cell>`
 * children carry authored content rather than a binding. Any `<fp-column>`
 * declarations act as a column ruler, supplying each cell's width, default
 * alignment and container style by position. The ruler is emitted as a
 * `<colgroup>` too, since a fixed layout otherwise sizes the columns from the
 * first row alone - and that row is often a single `span="all"` title.
 * @param rows - The table's gated `<fp-row>` children
 * @param layout - The ruler, rule style, and cell converter
 * @returns The table's column group and body
 */
function staticGrid(
  rows: FpGatedElement[],
  layout: StaticTableLayout,
): ReactElement {
  const gatedRows = rows.map((row) => ({
    hidden: row.hidden,
    nodeId: fpNodeId(row.element),
    cells: gateElements(
      directChildren(row.element, "fp-cell"),
      layout.gate,
      layout.editable,
    ),
  }));
  const grid = layoutStaticRows(gatedRows.map((row) => row.cells));
  const widest = grid.reduce(
    (maxWidth, row) => Math.max(maxWidth, row.width),
    Math.max(layout.ruler.length, 1),
  );
  const filler: ReactNode[] = layout.fillHeight
    ? [spacerRow(widest, layout.rule)]
    : [];
  const bodyRows = gatedRows.map((row, rowIndex) => {
    const rowGrid = grid[rowIndex];
    return (
      <tr
        key={rowIndex}
        data-fp-node={row.nodeId}
        className={hiddenClass(row.hidden)}
      >
        {row.cells.map((cell, cellIndex) =>
          staticCell(cell, cellIndex, {
            rowIndex,
            bottomEdge:
              rowIndex + rowGrid.rowspans[cellIndex] >= gatedRows.length &&
              !layout.fillHeight,
            lastColumn:
              (!rowGrid.hasCarryover && cellIndex === row.cells.length - 1) ||
              rowGrid.starts[cellIndex] + rowGrid.spans[cellIndex] >= widest,
            startColumn: rowGrid.starts[cellIndex],
            widest,
            layout,
          }),
        )}
      </tr>
    );
  });
  return (
    <>
      {columnGroup(widest, layout.ruler)}
      <tbody>
        {bodyRows}
        {filler}
      </tbody>
    </>
  );
}

/**
 * The table's column group, one `<col>` per grid column. A column the ruler
 * sizes holds that width; one it does not declare takes an equal share of what
 * is left, so a cell grows downwards as its content wraps rather than widening.
 * @param columnCount - The number of columns the table's grid covers
 * @param ruler - The table's `<fp-column>` declarations, by position
 * @returns The column group
 */
function columnGroup(
  columnCount: number,
  ruler: FpTableColumn[],
): ReactElement {
  return (
    <colgroup>
      {Array.from({ length: columnCount }, (_unused, index) => (
        <col key={index} style={{ width: ruler[index]?.width ?? undefined }} />
      ))}
    </colgroup>
  );
}

/** One static row's grid geometry, per cell index. */
interface StaticRowLayout {
  /** Each cell's starting grid column. */
  starts: number[];
  /** Each cell's numeric column span (a `span="all"` cell counts as one). */
  spans: number[];
  /** Each cell's row span. */
  rowspans: number[];
  /** The grid columns the row covers, rowspan carryover included. */
  width: number;
  /** True when a cell from an earlier row still covers part of this row. */
  hasCarryover: boolean;
}

/** The grid columns a rowspanning cell keeps covering on the rows below it. */
interface CarryoverSpan {
  start: number;
  span: number;
  /** The first row index the cell no longer covers. */
  until: number;
}

/**
 * Lays the static rows onto the table grid. A cell spanning rows keeps
 * covering its grid columns on the rows below it, so those rows' own cells
 * start past the covered columns instead of sliding underneath them. A cell
 * declaring `span="all"` counts as one column - it stretches to whatever width
 * the other rows establish, so measuring it as the full width would make the
 * table's own width circular.
 * @param rows - Each row's gated `<fp-cell>` children
 * @returns Each row's grid geometry, by row index
 */
function layoutStaticRows(rows: FpGatedElement[][]): StaticRowLayout[] {
  let active: CarryoverSpan[] = [];
  return rows.map((cells, rowIndex) => {
    const carryover = active.filter((entry) => entry.until > rowIndex);
    const occupied = new Set(
      carryover.flatMap((entry) =>
        Array.from(
          { length: entry.span },
          (_unused, offset) => entry.start + offset,
        ),
      ),
    );
    const layout: StaticRowLayout = {
      starts: [],
      spans: [],
      rowspans: [],
      width: carryover.reduce(
        (max, entry) => Math.max(max, entry.start + entry.span),
        0,
      ),
      hasCarryover: carryover.length > 0,
    };
    let position = 0;
    for (const cell of cells) {
      while (occupied.has(position)) {
        position += 1;
      }
      const { span, rowspan } = parseTableColumn(cell.element);
      const gridSpan = typeof span === "number" ? span : 1;
      layout.starts.push(position);
      layout.spans.push(gridSpan);
      layout.rowspans.push(rowspan);
      if (rowspan > 1) {
        carryover.push({
          start: position,
          span: gridSpan,
          until: rowIndex + rowspan,
        });
      }
      position += gridSpan;
      layout.width = Math.max(layout.width, position);
    }
    active = carryover;
    return layout;
  });
}

/** Where one static cell sits in its table, which decides its rules. */
interface StaticCellPosition {
  rowIndex: number;
  /**
   * Whether the cell reaches the table's own bottom edge - its row is the last,
   * or its rowspan runs to it - which the spacer row takes over when the table
   * fills its height.
   */
  bottomEdge: boolean;
  /** Whether the cell sits against the table's right edge. */
  lastColumn: boolean;
  /** The grid column the cell starts on, which selects its ruler column. */
  startColumn: number;
  /** The table's widest row, which every cell's span is measured against. */
  widest: number;
  layout: StaticTableLayout;
}

/**
 * One cell of a static row, merged with its positional ruler column.
 * @param cell - The gated `<fp-cell>` element
 * @param cellIndex - The cell's position in its row
 * @param position - Where the cell sits in the table
 * @returns The rendered cell
 */
function staticCell(
  cell: FpGatedElement,
  cellIndex: number,
  position: StaticCellPosition,
): ReactNode {
  const { layout } = position;
  const declared = parseTableColumn(cell.element);
  const guide = layout.ruler[position.startColumn];
  const column: FpTableColumn = {
    ...declared,
    width: declared.width ?? guide?.width ?? null,
    align: declared.align ?? guide?.align,
    style: { ...guide?.style, ...declared.style },
  };
  return (
    <td
      key={cellIndex}
      data-fp-node={column.nodeId}
      className={hiddenClass(cell.hidden)}
      colSpan={cellSpan(column, position.widest)}
      rowSpan={column.rowspan > 1 ? column.rowspan : undefined}
      style={cellStyle(
        column,
        layout.rule,
        !position.bottomEdge,
        position.lastColumn,
      )}
    >
      {staticCellContent(
        cell.element,
        `cell-${position.rowIndex}-${cellIndex}`,
        proseStyle(column.style),
        layout.renderChildren,
      )}
    </td>
  );
}

/**
 * The trailing row that soaks up a table's surplus height. A table given a
 * `height` taller than its content would otherwise stretch every row to share
 * the surplus; the original documents instead keep their rows at their natural
 * height and leave the remainder as empty ruled space below them.
 * @param columnCount - The number of columns the row must cover
 * @param rule - How the table draws the rules between its cells
 * @returns The spacer row
 */
function spacerRow(columnCount: number, rule: FpTableRuleStyle): ReactNode {
  return (
    <tr key="fp-fill" style={{ height: "100%" }}>
      {Array.from({ length: Math.max(columnCount, 1) }, (_unused, index) => (
        <td key={index} style={columnRule(rule, index === columnCount - 1)} />
      ))}
    </tr>
  );
}

/**
 * One static cell's content: markdown when the cell holds only authored text,
 * so a cell reads like every other authored body, and the converted child
 * elements when it nests fp-* primitives instead. The cell's text style rides
 * onto that markdown, which sets its own colour and font otherwise.
 *
 * A cell authored empty renders a bare non-breaking space. Standing an empty
 * `<fp-text>` in for it would draw that block's ghost hint, and a cell is not
 * a text block: the hint advertises a "]" menu the cell has no body to
 * receive, and a ruled band whose spacer cells each nag for a quote field
 * reads as broken. The space keeps the cell one text line tall instead of
 * collapsing to its padding, so it stays visible, clickable and type-able.
 * @param cell - The `<fp-cell>` element
 * @param keyPrefix - The React key prefix for converted children
 * @param textStyle - The cell's text style, forwarded to the rendered prose
 * @param renderChildren - Converts a cell's child nodes to React
 * @returns The cell's rendered content
 */
function staticCellContent(
  cell: Element,
  keyPrefix: string,
  textStyle: CSSProperties,
  renderChildren: (parent: Node, keyPrefix: string) => ReactNode[],
): ReactNode {
  if (cell.children.length > 0) {
    return renderChildren(cell, keyPrefix);
  }
  const markdown = cell.textContent ?? "";
  if (markdown.trim() === "") {
    return "\u00A0";
  }
  return <FpText source={null} content={markdown} style={textStyle} />;
}
